import {
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Eye, Lock01, Users01, X } from '@timelyapp/icons/line-icons';
import { Button } from '@timelyapp/react';
import {
  type AgentSession,
  type AssignProjectBody,
  getAgentSession,
} from 'api/agentSessions/agentSessions.api';
import TagFilterDropdown from 'components/dropdowns/TagFilterDropdown';
import { TimesheetProposalLinkedWorkSelect } from 'domains/ai/timesheet_proposals/components/TimesheetProposal.LinkedWorkSelect';
import { DateTime } from 'luxon';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getHasLinkedWorkPicker } from 'redux/modules/tic/tic.integrations.selectors';
import { getUser } from 'redux/selectors/user';
import { getUserEntities } from 'redux/selectors/users';
import { ConductorAvatar, ProviderIcon } from './AgentIdentity';
import AgentProjectPicker, { assignErrorMessage } from './AgentProjectPicker';
import AgentSessionActivity from './AgentSessionActivity';
import styles from './AgentSessionDetails.module.scss';
import {
  AgentLinkedWork,
  AgentTags,
  type AssignReview,
  type PublishSession,
  publishErrorMessage,
  useAgentTagOptions,
} from './AgentTableWorkspace';
import periodStyles from './AgentTrackingSessions.module.scss';
import AgentWorkPeriod, { ProjectLabel } from './AgentWorkPeriod';
import {
  type AgentPeriod,
  type AgentVisibility,
  describeModelUsage,
  formatCost,
  formatDuration,
  formatTokens,
  hasAgentMetric,
  modelUsage,
  sessionTitle,
  sharedProjectId,
  summarizePeriods,
  toAgentPeriods,
} from './agentTracking.model';

const dayLabel = (day: string) => DateTime.fromISO(day).toFormat('cccc, LLLL d, yyyy');

export const SHARING: Record<AgentVisibility, { icon: typeof Eye; label: string; text: string }> = {
  private: { icon: Lock01, label: 'Private', text: 'Only you can see this session.' },
  shared: {
    icon: Users01,
    label: 'Shared',
    text: "People who can see a project's hours can see the periods you put on it.",
  },
  published: {
    icon: Eye,
    label: 'Published',
    text: 'People who can see your work in Reports can see this session.',
  },
};

/** What the sharing states mean to someone looking at a colleague's session (TT-6346). */
const COLLEAGUE_SHARING: Record<AgentVisibility, string> = {
  private: 'Only its owner can see this session.',
  shared: "People who can see a project's hours can see the periods put on it.",
  published: "People who can see this person's work in Reports can see this session.",
};

function SharingState({ visibility, own }: { visibility: AgentVisibility; own: boolean }) {
  const { icon: Icon, label } = SHARING[visibility];
  const text = own ? SHARING[visibility].text : COLLEAGUE_SHARING[visibility];
  return (
    <p className={styles.sharingState}>
      <Icon width={16} height={16} />
      <span>
        <strong>{label}</strong>
        <span>{text}</span>
      </span>
    </p>
  );
}

/** An owned session is fetched independently of the table's date/project window. */
export default function AgentSessionDetails({
  sessionId,
  onAssign,
  onPublish,
  load,
  children,
}: {
  sessionId: number;
  onAssign?: AssignReview;
  onPublish?: PublishSession;
  load?: typeof getAgentSession;
  children: (open: () => void) => React.ReactElement;
}) {
  const [open, setOpen] = useState(false);
  const target = useRef<HTMLSpanElement>(null);
  const nodeId = useFloatingNodeId();
  const { refs, context } = useFloating({ open, onOpenChange: changeOpen, nodeId });
  const { getFloatingProps } = useInteractions([useDismiss(context), useRole(context)]);
  function changeOpen(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) {
      requestAnimationFrame(() => target.current?.querySelector('button')?.focus());
    }
  }
  function close() {
    changeOpen(false);
  }
  return (
    <FloatingNode id={nodeId}>
      <span ref={target}>{children(() => requestAnimationFrame(() => setOpen(true)))}</span>
      {open && (
        <FloatingPortal>
          <FloatingOverlay lockScroll className={styles.overlay} data-agent-session-overlay>
            <FloatingFocusManager
              context={context}
              initialFocus={refs.floating}
              returnFocus={false}
            >
              <div
                {...getFloatingProps({
                  ref: refs.setFloating,
                  className: styles.dialog,
                  'aria-label': 'Session details',
                  'aria-modal': true,
                })}
              >
                <AgentSessionDetailsCard
                  embedded
                  load={load}
                  sessionId={sessionId}
                  onAssign={onAssign}
                  onPublish={onPublish}
                  onClose={close}
                />
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </FloatingNode>
  );
}

export function AgentSessionDetailsCard({
  sessionId,
  embedded = false,
  onAssign,
  onPublish,
  onClose,
  load = getAgentSession,
}: {
  sessionId: number;
  /**
   * Save the edits through the list that owns the session, which runs them in
   * order with its other changes and patches only the fields saved. Without it
   * the session cannot be edited.
   */
  onAssign?: AssignReview;
  /**
   * Publish or make the session private through the list that owns it, so that
   * list also learns about the change. Without it the session can only be viewed.
   */
  onPublish?: PublishSession;
  onClose: () => void;
  embedded?: boolean;
  load?: typeof getAgentSession;
}) {
  const headingId = useId();
  const [session, setSession] = useState<AgentSession | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [patch, setPatch] = useState<AssignProjectBody>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pendingPublished, setPendingPublished] = useState<boolean | null>(null);
  const [publishError, setPublishError] = useState('');
  // A save and a publish both return the whole session, so they run one at a time.
  const busy = saving || pendingPublished !== null;
  const closeButton = useRef<HTMLButtonElement>(null);
  const users = useSelector(getUserEntities);
  const viewer = useSelector(getUser);
  const hasLinkedWork = useSelector(getHasLinkedWorkPicker);

  // biome-ignore lint/correctness/useExhaustiveDependencies: retry explicitly reloads the session
  useEffect(() => {
    let cancelled = false;
    setSession(null);
    setError('');
    setPatch({});
    setSaved(false);
    closeButton.current?.focus();
    load(sessionId)
      .then(value => {
        if (!cancelled) setSession(value);
      })
      .catch(e => {
        if (!cancelled) setError(assignErrorMessage(e, 'Loading session'));
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, load, attempt]);

  function edit(update: AssignProjectBody) {
    setPatch(previous => ({ ...previous, ...update }));
    setSaved(false);
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !onAssign || !Object.keys(patch).length) return;
    setSaving(true);
    setError('');
    try {
      // The owner omits segment_ids: the server updates all existing days atomically.
      // Someone else edits only the periods they were shown (TT-6346).
      const body =
        own || !('project_id' in patch || 'label_ids' in patch || 'resource_id' in patch)
          ? patch
          : { ...patch, segment_ids: session?.segments.map(segment => segment.id) ?? [] };
      setSession(await onAssign(sessionId, body));
      setPatch({});
      setSaved(true);
    } catch (e) {
      setError(assignErrorMessage(e, 'Saving session'));
    } finally {
      setSaving(false);
    }
  }

  async function changePublished(next: boolean) {
    if (!onPublish || busy) return;
    setPendingPublished(next);
    setPublishError('');
    try {
      setSession(await onPublish(sessionId, next));
    } catch {
      setPublishError(publishErrorMessage(next));
    } finally {
      setPendingPublished(null);
    }
  }

  const conductor = session
    ? (users[session.user_id] ?? (viewer.id === session.user_id ? viewer : undefined))
    : undefined;
  const own = !session || session.user_id === viewer.id;
  // Rails' can_edit; an older Rails leaves it out and lets only the owner edit.
  const canRename = !!onAssign && (session?.can_edit ?? own);
  const canAssignAll =
    !!onAssign &&
    !!session?.segments.length &&
    session.segments.every(segment => segment.can_edit ?? own);
  const periods = session ? toAgentPeriods([session]) : [];
  const tags = useAgentTagOptions(periods, patch.project_id);
  const days = [...new Set(periods.map(period => period.date))].sort((a, b) => b.localeCompare(a));
  const projectId = patch.project_id !== undefined ? patch.project_id : sharedProjectId(periods);
  const resources = [...new Set(periods.map(period => period.resourceId ?? null))];
  const resourceId =
    patch.resource_id !== undefined
      ? patch.resource_id
      : resources.length === 1
        ? resources[0]
        : null;
  const labelSets = [
    ...new Set(periods.map(period => [...(period.labelIds ?? [])].sort((a, b) => a - b).join(','))),
  ];
  const selectedLabels =
    patch.label_ids ?? (labelSets.length === 1 ? (periods[0]?.labelIds ?? []) : []);
  const models = modelUsage(periods);
  const tools =
    session?.segments.reduce<Record<string, number>>((counts, segment) => {
      Object.entries(segment.tool_counts).forEach(([tool, count]) => {
        counts[tool] = (counts[tool] ?? 0) + count;
      });
      return counts;
    }, {}) ?? {};

  return (
    <section
      role={embedded ? 'document' : 'dialog'}
      aria-labelledby={headingId}
      className={styles.card}
    >
      <header className={styles.header}>
        <h2 id={headingId}>Session details</h2>
        <button
          ref={closeButton}
          type="button"
          aria-label="Close session details"
          onClick={onClose}
          disabled={saving}
          className={styles.close}
        >
          <X width={20} height={20} />
        </button>
      </header>
      {!session ? (
        <div className={styles.loading}>
          {error ? (
            <>
              <p role="alert">{error}</p>
              <Button onClick={() => setAttempt(value => value + 1)}>Try again</Button>
            </>
          ) : (
            <p role="status">Loading the complete session…</p>
          )}
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className={styles.content}>
            <div className={styles.overview}>
              <div className={styles.sessionSummary}>
                <div className={styles.titleRow}>
                  <ProviderIcon
                    provider={session.provider}
                    name={session.provider_name}
                    className={styles.titleProviderIcon}
                  />
                  <label className={styles.titleLabel}>
                    <span className={styles.titleHint}>
                      Session title {canRename && <span>Click to rename</span>}
                    </span>
                    <input
                      disabled={busy || !canRename}
                      aria-label="Session title"
                      value={patch.title ?? sessionTitle(session)}
                      maxLength={500}
                      required
                      onChange={event => edit({ title: event.target.value })}
                    />
                  </label>
                </div>
                <div className={styles.identities}>
                  <div className={styles.identity}>
                    <span>
                      <small>Provider</small>
                      {session.provider_name}
                    </span>
                  </div>
                  <div className={styles.identity}>
                    <ConductorAvatar userId={session.user_id} />
                    <span>
                      <small>Conductor</small>
                      {conductor?.name || `User ${session.user_id}`}
                    </span>
                  </div>
                  <span className={styles.activityCount}>
                    {days.length} {days.length === 1 ? 'day' : 'days'} · {periods.length}{' '}
                    {periods.length === 1 ? 'period' : 'periods'}
                  </span>
                </div>
                <SessionMetrics periods={periods} />
              </div>
              <AgentSessionActivity periods={periods} />
            </div>
            <div className={styles.workspace}>
              <aside className={styles.sidebar} aria-label="Session settings">
                <div className={styles.sharing}>
                  <h3>Sharing</h3>
                  <SharingState visibility={session.visibility} own={own} />
                  {onPublish && (
                    <div className={styles.sharingActions}>
                      {session.visibility !== 'published' && (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => void changePublished(true)}
                        >
                          {pendingPublished === true ? 'Publishing…' : 'Publish'}
                        </Button>
                      )}
                    </div>
                  )}
                  {publishError && <p role="alert">{publishError}</p>}
                </div>
                <fieldset disabled={busy || !canAssignAll} className={styles.editFields}>
                  <div className={styles.assignmentHeader}>
                    <h3>Session assignments</h3>
                    <p>Applies to all {periods.length} periods, across every day.</p>
                  </div>
                  <div className={styles.assignments}>
                    <div>
                      <span>Project</span>
                      <AgentProjectPicker
                        value={projectId}
                        placeholder={
                          projectId === null &&
                          periods.some(p => p.projectId !== null) &&
                          patch.project_id === undefined
                            ? 'Mixed projects'
                            : 'Choose project'
                        }
                        ariaLabel="Session project"
                        ownerId={session.user_id}
                        onChange={async project_id => edit({ project_id, label_ids: undefined })}
                      />
                      {/* Clearing leaves unassigned periods, which a manager may not change. */}
                      {(own || session.can_edit) && (
                        <button
                          className={styles.clear}
                          type="button"
                          onClick={() => edit({ project_id: null, label_ids: undefined })}
                        >
                          Clear project
                        </button>
                      )}
                    </div>
                    <div>
                      <span>Linked work</span>
                      {hasLinkedWork ? (
                        <TimesheetProposalLinkedWorkSelect
                          value={resourceId}
                          placeholder={
                            resources.length > 1 && patch.resource_id === undefined
                              ? 'Mixed linked work'
                              : 'Choose linked work'
                          }
                          onChange={resource_id => edit({ resource_id })}
                          disabled={busy}
                          projectId={projectId}
                          asButton
                        />
                      ) : (
                        <p>Connect an integration to choose linked work.</p>
                      )}
                      <button
                        className={styles.clear}
                        type="button"
                        onClick={() => edit({ resource_id: null })}
                      >
                        Clear linked work
                      </button>
                    </div>
                    <div>
                      <span>Tags</span>
                      <TagFilterDropdown
                        tags={tags}
                        disabled={!tags.length}
                        selected={selectedLabels}
                        onChange={label_ids => edit({ label_ids })}
                        batchMode
                        disableSelectNone
                        singleChildSelect
                        emptyLabel={
                          !tags.length
                            ? 'No tags available'
                            : labelSets.length > 1 && !patch.label_ids
                              ? 'Mixed tags'
                              : 'Choose tags'
                        }
                        size="small"
                      />
                      <button
                        className={styles.clear}
                        type="button"
                        onClick={() => edit({ label_ids: [] })}
                      >
                        Clear tags
                      </button>
                    </div>
                  </div>
                </fieldset>
                <details className={styles.technical}>
                  <summary>Session context & usage</summary>
                  <dl>
                    <dt>Provider</dt>
                    <dd>{session.provider_name}</dd>
                    <dt>Session ID</dt>
                    <dd>{session.provider_session_id}</dd>
                    {session.folder_name && (
                      <>
                        <dt>Folder</dt>
                        <dd>{session.folder_name}</dd>
                      </>
                    )}
                    {session.git_branch && (
                      <>
                        <dt>Branch</dt>
                        <dd>{session.git_branch}</dd>
                      </>
                    )}
                    {session.git_remote && (
                      <>
                        <dt>Repository</dt>
                        <dd>{session.git_remote}</dd>
                      </>
                    )}
                    {session.started_at && (
                      <>
                        <dt>First activity</dt>
                        <dd>
                          {DateTime.fromISO(session.started_at, { setZone: true }).toFormat(
                            'LLL d, yyyy · HH:mm ZZZZ'
                          )}
                        </dd>
                      </>
                    )}
                    {session.ended_at && (
                      <>
                        <dt>Latest activity</dt>
                        <dd>
                          {DateTime.fromISO(session.ended_at, { setZone: true }).toFormat(
                            'LLL d, yyyy · HH:mm ZZZZ'
                          )}
                        </dd>
                      </>
                    )}
                  </dl>
                  {!!models.length && (
                    <>
                      <h4>Models</h4>
                      <ul>
                        {models.map(model => (
                          <li key={model.model}>
                            <strong>{model.label}</strong> · {formatTokens(model.tokens)} tokens{' '}
                            {describeModelUsage(model) && `· ${describeModelUsage(model)}`}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <dl>
                    {(
                      [
                        'input_tokens',
                        'output_tokens',
                        'cache_read_tokens',
                        'cache_write_tokens',
                      ] as const
                    ).map((key, index) => (
                      <React.Fragment key={key}>
                        <dt>
                          {
                            [
                              'Input tokens',
                              'Output tokens',
                              'Cache read tokens',
                              'Cache write tokens',
                            ][index]
                          }
                        </dt>
                        <dd>{tokenDetail(periods, key)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                  {!!Object.keys(tools).length && (
                    <>
                      <h4>Tool calls</h4>
                      <ul>
                        {Object.entries(tools)
                          .sort((a, b) => b[1] - a[1])
                          .map(([tool, count]) => (
                            <li key={tool}>
                              {tool} <strong>{count}</strong>
                            </li>
                          ))}
                      </ul>
                    </>
                  )}
                  <p className={styles.subtitle}>
                    Times use the recorded time zone. Cost is an API-price estimate, not a provider
                    bill. Unavailable measurements are shown as —.
                  </p>
                </details>
              </aside>
              <section className={styles.timeline} aria-label="All session periods">
                <h3>
                  Work periods <span>{periods.length}</span>
                </h3>
                <p className={styles.subtitle}>
                  {days.length} {days.length === 1 ? 'day' : 'days'} of activity · Newest first
                </p>
                {!days.length && <p>No work periods recorded.</p>}
                {days.map(day => (
                  <section key={day} className={styles.day}>
                    <h4>{dayLabel(day)}</h4>
                    <ol className={periodStyles.activityList}>
                      {periods
                        .filter(period => period.date === day)
                        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
                        .map(period => (
                          <AgentWorkPeriod
                            key={period.id}
                            period={period}
                            compact
                            onAssign={
                              own && onAssign
                                ? async (id, patch) => {
                                    const updated = await onAssign(id, patch);
                                    setSession(updated);
                                  }
                                : undefined
                            }
                            project={
                              <ProjectLabel
                                projectId={period.projectId}
                                fallbackName={
                                  period.projectId === null
                                    ? 'Unassigned'
                                    : (period.projectName ?? 'Unavailable project')
                                }
                                fallbackColor={period.projectColor}
                              />
                            }
                            links={
                              <>
                                <AgentTags periods={[period]} />
                                {period.resourceId && <AgentLinkedWork periods={[period]} />}
                              </>
                            }
                          />
                        ))}
                    </ol>
                  </section>
                ))}
              </section>
            </div>
          </div>
          <footer className={styles.footer}>
            {error && <p role="alert">{error}</p>}
            <span role="status">
              {saved
                ? 'Session updated across all days.'
                : Object.keys(patch).length
                  ? 'Unsaved changes · all days'
                  : 'Changes apply to the entire session'}
            </span>
            <Button
              type="submit"
              disabled={
                busy ||
                !Object.keys(patch).length ||
                (patch.title !== undefined && !patch.title.trim())
              }
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </footer>
        </form>
      )}
    </section>
  );
}

function tokenDetail(
  periods: AgentPeriod[],
  key: 'input_tokens' | 'output_tokens' | 'cache_read_tokens' | 'cache_write_tokens'
) {
  const field = {
    input_tokens: 'inputTokens',
    output_tokens: 'outputTokens',
    cache_read_tokens: 'cacheReadTokens',
    cache_write_tokens: 'cacheWriteTokens',
  } as const;
  const measured = periods.filter(period => hasAgentMetric(period, key));
  return measured.length
    ? formatTokens(measured.reduce((sum, period) => sum + period[field[key]], 0))
    : '—';
}

function SessionMetrics({ periods }: { periods: AgentPeriod[] }) {
  const total = summarizePeriods(periods);
  const metric = (key: 'seconds' | 'tokens' | 'cost', value: string) => {
    return periods.some(period => hasAgentMetric(period, key)) ? value : '—';
  };
  return (
    <dl className={styles.metrics}>
      <div>
        <dt>Agent time</dt>
        <dd>{metric('seconds', formatDuration(total.seconds))}</dd>
      </div>
      <div>
        <dt>Tokens</dt>
        <dd>{metric('tokens', formatTokens(total.tokens))}</dd>
      </div>
      <div>
        <dt>Est. cost</dt>
        <dd>{metric('cost', formatCost(total.cost))}</dd>
      </div>
    </dl>
  );
}
